import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useSearchParams } from '@remix-run/react';
import * as React from 'react';
import { PAGE_PARAM, SORT_PARAM } from '~/utils/searchParams';
import { MuiRemixLink } from './links';

export type Options = { [key: string]: string };

export function getSortParamValue<T extends Options>(
  options: T,
  defaultOption: keyof T,
  searchParams: URLSearchParams
) {
  const value = searchParams.get(SORT_PARAM) ?? '';
  return options[value] ? value : defaultOption;
}

export function SortBy<T extends Options>({
  options,
  defaultOption,
  fullWidth = false,
  noBorder = false,
}: {
  options: T;
  defaultOption: keyof T;
  fullWidth?: boolean;
  noBorder?: boolean;
}) {
  const [searchParams] = useSearchParams();
  const [sortValue, setSortValue] = React.useState(() => getSortParamValue(options, defaultOption, searchParams));

  React.useEffect(() => {
    setSortValue(getSortParamValue(options, defaultOption, searchParams));
  }, [defaultOption, options, searchParams]);

  const handleChange = (event: SelectChangeEvent) => {
    setSortValue(event.target.value as keyof T);
  };

  const getUrl = (sortOptionValue: keyof T) => {
    const linkSearchParams = new URLSearchParams(searchParams);
    linkSearchParams.delete(PAGE_PARAM);
    if (sortOptionValue === defaultOption) {
      linkSearchParams.delete(SORT_PARAM);
    } else {
      linkSearchParams.set(SORT_PARAM, sortOptionValue as string);
    }
    linkSearchParams.sort();
    return '?' + linkSearchParams;
  };

  return (
    <FormControl size="small" fullWidth={fullWidth}>
      <Select
        value={sortValue as string}
        renderValue={(value) => <Typography variant="body2"> {'Sort by: ' + options[value]}</Typography>}
        onChange={handleChange}
        fullWidth={fullWidth}
        sx={{ '& fieldset': { ...(noBorder ? { border: 'none' } : null) } }}
      >
        {Object.entries(options).map(([sortOptionValue, text]) => {
          return (
            <MenuItem key={sortOptionValue} value={sortOptionValue} sx={{ p: 0 }}>
              <MuiRemixLink
                href={getUrl(sortOptionValue)}
                underline="none"
                variant="body2"
                color="inherit"
                sx={{ width: '100%', py: 0.75, px: 2 }}
              >
                {text}
              </MuiRemixLink>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
